import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { FaTrash } from 'react-icons/fa';
import { SpinnerCircular } from 'spinners-react';
import history from 'routing/history';
import Button, { BackButton } from 'components/Button';
import UploadInstructions from './components/UploadInstructions';
import ViewAnimator from '../../components/ViewAnimator';
import useNextAndPrevUrl from '../../hooks/useNextAndPrevUrl';
import { addValidStep, sendFile, removeFile } from '../../state';
import { STEP_PATHS } from '../../consts';
import { IntercomLink, ButtonUpload } from './styled';
import { StepHeading, Instructions, NavigationButtons, File, Alert } from '../../styled';

const acceptedFiletypes = '.jpg, .jpeg, .png, .pdf';

const UploadDocuments = () => {
  const dispatch = useDispatch();
  const { claimType, step } = useParams();
  const { nextUrl, prevUrl } = useNextAndPrevUrl(claimType, step);
  const { claimFiles } = useSelector((state) => state.nomadInsurance.claims.claimInfo);
  const {
    claimID,
    deletingFileID,
    isSendingFiles,
    isDeletingFile,
    documentErrors,
    isLoading
  } = useSelector((state) => state.nomadInsurance.claims);

  useEffect(() => {
    dispatch(addValidStep(STEP_PATHS.UPLOAD_DOCUMENTS));
  }, [dispatch]);

  return (
    <ViewAnimator>
      <StepHeading>Upload documents</StepHeading>
      <div>
        <UploadInstructions claimType={claimType} />
        <Instructions>
          <IntercomLink onClick={() => window.Intercom('showNewMessage')}>
            Reach out
          </IntercomLink>{' '}
          if you’re unsure about what documentation you need.
        </Instructions>
        {claimFiles.map((file) => (
          <File key={file.id} color="turquoise">
            <span>{file.userFileName || file.name}</span>
            {!isLoading && (
              <FaTrash
                onClick={() => {
                  dispatch(removeFile(file.id));
                }}
              />
            )}
            {isLoading && deletingFileID === file.id && (
              <SpinnerCircular
                size={20}
                thickness={150}
                speed={100}
                color="rgba(92, 198, 199, 1)"
                secondaryColor="rgba(0, 0, 0, 0.10)"
              />
            )}
          </File>
        ))}
        {documentErrors.map((documentError) => (
          <Alert
            key={documentError.fileName}
            variant="danger"
            label="Something went wrong"
          >
            Something went wrong with saving the file {documentError.fileName}. Please try
            again, and if the problem persists, contact customer service.
          </Alert>
        ))}
        <ButtonUpload
          multiple
          label="Upload"
          isUploading={isSendingFiles?.length > 0 || isLoading}
          isUploaded={isDeletingFile}
          onUpload={(file) => dispatch(sendFile({ file, claimID }))}
          showButtonOnRemoving
          acceptedFiletypes={acceptedFiletypes}
        />
      </div>
      <NavigationButtons>
        <BackButton onClick={() => history.push(prevUrl)} variant="dark">
          Back
        </BackButton>
        <Button
          onClick={() => history.push(nextUrl)}
          disabled={
            isSendingFiles?.length > 0 ||
            isDeletingFile ||
            isLoading ||
            claimFiles.length === 0
          }
        >
          Next
        </Button>
      </NavigationButtons>
    </ViewAnimator>
  );
};

export default UploadDocuments;
